import type {FC} from 'react';
import clsx from 'clsx';
import Link from 'next/link';

import {Badge} from '@/components/badge';
import ImageFill from '@/components/image/ImageFill';
import type {ImageWeb} from '@/components/image/types';

export type LinkCardProps = {
	href: string;
	title: string;
	image?: ImageWeb | null;
	description?: string;
	headingLevel?: 2 | 3;
	tags?: string[];
	date?: string;
	aspectRatio?: 'normal' | 'wide';
};

export const LinkCard: FC<LinkCardProps> = ({
	image,
	href,
	title,
	description,
	headingLevel = 2,
	tags,
	date,
	aspectRatio = 'normal',
}) => {
	return (
		<Link href={href} className={clsx('flex', 'flex-col', 'group')}>
			<div
				className={clsx(
					'relative',
					aspectRatio === 'wide' ? 'aspect-[2/1]' : 'aspect-[16/9]',
					'rounded-xl',
					'overflow-hidden',
					'bg-[var(--ds-color-surface-tinted)]',
					'mb-3',
				)}
			>
				{image && (
					<ImageFill
						src={image.image.src}
						width={image.image.width}
						height={image.image.height}
						hotspot={image.image.hotspot}
						alt={image.altText ?? ''}
						className={clsx(
							'group-hover:scale-105',
							'transition-transform',
						)}
					/>
				)}
			</div>
			{(date || tags) && (
				<div
					className={clsx(
						'flex',
						'justify-between',
						'gap-x-4',
						'gap-y-2',
						'flex-wrap',
						'items-center',
						'mb-2',
					)}
				>
					{tags &&
						tags.length > 0 &&
						tags.map((tag, idx) => (
							<Badge
								key={tag + idx}
								variant="secondary"
								size="small"
							>
								{tag}
							</Badge>
						))}
					{date && (
						<span
							className={clsx(
								'text-secondary-text-subtle',
								'text-body-short-sm',
							)}
						>
							{date}
						</span>
					)}
				</div>
			)}
			{headingLevel === 2 ? (
				<h2
					className={clsx(
						'font-semibold',
						'self-start',
						'text-heading-xs',
						'font-display',
						'mb-2',
						'relative',
						'after:absolute',
						'after:bottom-[-3px]',
						'after:left-0',
						'after:bg-primary-text-default',
						'after:h-[3px]',
						'group-hover:after:w-full',
						'group-hover:text-primary-text-default',
						'transition-colors',
					)}
				>
					{title}
				</h2>
			) : (
				<h3
					className={clsx(
						'font-semibold',
						'self-start',
						'text-heading-xs',
						'font-display',
						'mb-2',
						'relative',
						'after:absolute',
						'after:bottom-[-3px]',
						'after:left-0',
						'after:bg-primary-text-default',
						'after:h-[3px]',
						'group-hover:after:w-full',
						'group-hover:text-primary-text-default',
						'transition-colors',
					)}
				>
					{title}
				</h3>
			)}

			{description && (
				<p className={clsx('text-body-md')}>{description}</p>
			)}
		</Link>
	);
};
